import { readQueryParam } from "../utils/readQueryParam";
import type { Log } from "@slot/presentation";

/**
 * What the casino's game link says (decision 81, the backend's ADR 0072): `<client>?launch=<token>&lang=<code>&
 * currency=<code>`. `launch` is the credential the play session starts with, opaque, valid for a day and for any
 * number of sessions, so it stays in the address bar for a reload; `currency` is a free-play backend's currency for its
 * anonymous player; `lang` is the player's language. A value of the wrong shape is ignored and logged (the launch by
 * name only, never its value).
 */
export interface GameLink {
  launch: string | null;
  currency: string | null;
  lang: string | null;
}

/** A launch is opaque (do not depend on its shape, the backend asks), only bounded: something, no whitespace. */
const MAX_LAUNCH_LENGTH = 4096;

export function readGameLink(queryString: string | null, log: Log): GameLink {
  const launch = readQueryParam(queryString, "launch");
  const currency = readQueryParam(queryString, "currency");
  const lang = readQueryParam(queryString, "lang");
  const link: GameLink = {
    launch: launch !== null && launch.length > 0 && launch.length <= MAX_LAUNCH_LENGTH && !/\s/.test(launch) ? launch : null,
    currency: currency !== null && /^[A-Z]{3}$/.test(currency) ? currency : null,
    lang: lang !== null && /^[A-Za-z]{2,3}(?:[-_][A-Za-z0-9]{2,8})*$/.test(lang) ? lang : null,
  };
  if (launch !== null && link.launch === null) log("[GameLink] ignored a malformed ?launch");
  if (currency !== null && link.currency === null) log(`[GameLink] ignored ?currency="${currency}"`);
  if (lang !== null && link.lang === null) log(`[GameLink] ignored ?lang="${lang}"`);
  return link;
}
